import { useEffect, useState } from "react";

const GENERATED_HTML_KEY = "banner-generated-html";

const HtmlPreview = ({
  elements,
  generateHTML,
  setGenerateHTML,
  setGeneratedHTML,
  onGenerated,
}) => {
  const [htmlCode, setHtmlCode] = useState("");

  /* =========================================================
     ESCAPE ATTRIBUTE
  ========================================================= */

  const escapeAttribute = (value = "") =>
    String(value)
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

  /* =========================================================
     BUILD HTML
  ========================================================= */

  const buildHTML = () => {
    const boxes = elements.filter((el) => el.type === "box");

    if (!boxes.length) {
      return;
    }

    const minBoxX = Math.min(...boxes.map((box) => box.x || 0));

    const maxBoxRight = Math.max(
      ...boxes.map((box) => (box.x || 0) + (box.width || 0)),
    );

    const maxBoxBottom = Math.max(
      ...boxes.map((box) => (box.y || 0) + (box.height || 0)),
    );

    const bannerWidth = Math.max(300, maxBoxRight - minBoxX + 80);

    const bannerHeight = Math.max(300, maxBoxBottom);

    const bannerElements = boxes
      .map((box) => {
        const boxLeft = (box.x || 0) - minBoxX;

        const boxTop = Math.max(0, (box.y || 0) - 20);

        const boxStyle = `
          position:absolute;
          left:${boxLeft}px;
          top:${boxTop}px;
          width:${box.width}px;
          height:${box.height}px;
          background:${box.background || "transparent"};
          background-image:${
            box.backgroundImage
              ? `url("${escapeAttribute(box.backgroundImage)}")`
              : "none"
          };
          background-size:${box.backgroundSize || "cover"};
          background-position:${box.backgroundPosition || "center"};
          background-repeat:${box.backgroundRepeat || "no-repeat"};
          border-radius:${box.borderRadius || 0}px;
          padding:${box.padding || 0}px;
          z-index:${box.zIndex || 1};
          opacity:${box.opacity ?? 1};
          overflow:hidden;
          box-sizing:border-box;
        `;

        const children = elements.filter((el) => el.parentBox === box.id);

        const childrenHTML = children
          .map((el) => {
            const relativeX = (el.x || 0) - (box.x || 0);

            const relativeY = (el.y || 0) - (box.y || 0);

            const baseStyle = `
              position:absolute;
              left:${relativeX}px;
              top:${relativeY}px;
              width:${el.width}px;
              height:${el.height}px;
              border-radius:${el.borderRadius || 0}px;
              padding:${el.padding || 0}px;
              z-index:${el.zIndex || 1};
              opacity:${el.opacity ?? 1};
              transform:
                rotate(${el.rotation || 0}deg)
                scaleX(${el.scaleX || 1})
                scaleY(${el.scaleY || 1});
              box-sizing:border-box;
            `;

            /* ================= HEADING ================= */

            if (el.type === "heading") {
              return `
                <div
                  style="
                    ${baseStyle}
                    display:flex;
                    align-items:center;
                    justify-content:${
                      el.textAlign === "center"
                        ? "center"
                        : el.textAlign === "right"
                          ? "flex-end"
                          : "flex-start"
                    };
                    font-size:${el.fontSize || "16px"};
                    font-family:${el.fontFamily || "Inter"};
                    font-weight:${el.fontWeight || "normal"};
                    font-style:${el.fontStyle || "normal"};
                    text-decoration:${el.textDecoration || "none"};
                    text-align:${el.textAlign || "left"};
                    color:${el.color || "#000000"};
                    line-height:1.1;
                    overflow:hidden;
                  "
                >
                  ${el.text || ""}
                </div>
              `;
            }

            /* ================= BUTTON ================= */

            if (el.type === "button") {
              const buttonHTML = `
                <button
                  style="
                    width:100%;
                    height:100%;
                    background:${el.background || "#000000"};
                    color:${el.color || "#ffffff"};
                    border:none;
                    border-radius:${el.borderRadius || 8}px;
                    cursor:pointer;
                    font-size:${el.fontSize || "16px"};
                    font-family:${el.fontFamily || "Inter"};
                    font-weight:${el.fontWeight || "normal"};
                    font-style:${el.fontStyle || "normal"};
                    text-decoration:${el.textDecoration || "none"};
                  "
                >
                  ${el.text || ""}
                </button>
              `;

              if (el.link) {
                return `
                  <a
                    href="${escapeAttribute(el.link)}"
                    target="_blank"
                    rel="noopener noreferrer"
                    style="
                      ${baseStyle}
                      display:block;
                      text-decoration:none;
                    "
                  >
                    ${buttonHTML}
                  </a>
                `;
              }

              return `
                <div
                  style="
                    ${baseStyle}
                    display:block;
                  "
                >
                  ${buttonHTML}
                </div>
              `;
            }

            /* ================= IMAGE ================= */

            if (el.type === "image") {
              const imageHTML = `
                <img
                  src="${escapeAttribute(el.src)}"
                  alt=""
                  style="
                    width:100%;
                    height:100%;
                    object-fit:cover;
                    border-radius:${el.borderRadius || 0}px;
                    display:block;
                  "
                />
              `;

              if (el.link) {
                return `
                  <a
                    href="${escapeAttribute(el.link)}"
                    target="_blank"
                    rel="noopener noreferrer"
                    style="
                      ${baseStyle}
                      display:block;
                    "
                  >
                    ${imageHTML}
                  </a>
                `;
              }

              return `
                <div
                  style="
                    ${baseStyle}
                    display:block;
                  "
                >
                  ${imageHTML}
                </div>
              `;
            }

            return "";
          })
          .join("");

        return `
          <div style="${boxStyle}">
            ${childrenHTML}
          </div>
        `;
      })
      .join("");

    const fullHTML = `
<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<title>Banner</title>

<style>

html,
body {
  margin: 0;
  padding: 0;
}

body {
  margin: 0;
  background: transparent;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  overflow: auto;
}

.bannerrs {
  position: relative;
  width: ${bannerWidth}px;
  height: ${bannerHeight}px;
  border-radius: 12px;
}

</style>

</head>

<body>

<div class="bannerrs">
  ${bannerElements}
</div>

</body>
</html>
`;

    const oneLineHTML = fullHTML.replace(/\n/g, "").replace(/\s+/g, " ").trim();

    setHtmlCode(oneLineHTML);

    /* Global context */

    if (setGeneratedHTML) {
      setGeneratedHTML(oneLineHTML);
    }

    sessionStorage.setItem(GENERATED_HTML_KEY, oneLineHTML);

    navigator.clipboard?.writeText(oneLineHTML).catch(() => {});
    onGenerated?.(oneLineHTML);
  };

  useEffect(() => {
    if (!generateHTML) return;

    buildHTML();

    setGenerateHTML(false);
  }, [generateHTML]);

  return null;
};

export default HtmlPreview;
